/**
 * Firestore implementation of OrderRepository.
 *
 * Path convention: orders/{orderId}
 */

import {
  collection,
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  limit as fsLimit,
} from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import type { Order, EntityId } from '../../../types';
import type { OrderRepository, OrderListFilter } from '../types';
import { normalizeOrderStatus } from '../../../modules/orders/services/fulfillmentStage';

// Orders live in the `orders` collection (Projects→Orders rename, PR2). The
// legacy `projects` collection was read as a fallback during the migration
// window; that dual-source read was retired 2026-09-04 (backlog #80) once prod
// held ZERO `projects` docs — it had been doubling every order list as two
// SEQUENTIAL queries, the second always empty. `toOrder` still normalises the
// legacy field names / status values, so an un-migrated doc would still render;
// it just has to live in `orders` to be found.
const COLLECTION = 'orders';

/**
 * Normalize a raw order doc to the current field names + status enum:
 * - Projects→Orders PR3: legacy docs carry `projectNumber`; map it to
 *   `orderNumber`.
 * - Slice 1b: legacy docs carry pre-migration status values
 *   (OPEN/QUOTED/WORK_ORDER_SENT/CLOSED/LOST); `normalizeOrderStatus` maps them
 *   to the canonical enum so the whole app is correct on un-migrated data.
 * Backfill / `migrateOrderStatuses` rewrite the stored fields; this covers the
 * window in between.
 */
const toOrder = (data: Record<string, unknown>): Order => {
  const orderNumber = data.orderNumber ?? data.projectNumber;
  return {
    ...data,
    ...(orderNumber != null ? { orderNumber } : {}),
    status: normalizeOrderStatus(typeof data.status === 'string' ? data.status : ''),
  } as unknown as Order;
};

export const firestoreOrderRepository: OrderRepository = {
  async findById(id: EntityId): Promise<Order | null> {
    const snap = await getDoc(doc(db, COLLECTION, id));
    return snap.exists() ? toOrder(snap.data()) : null;
  },

  async save(entity: Order): Promise<void> {
    await setDoc(doc(db, COLLECTION, entity.id), entity);
  },

  async delete(id: EntityId): Promise<void> {
    await deleteDoc(doc(db, COLLECTION, id));
  },

  async list(filter?: OrderListFilter): Promise<Order[]> {
    const constraints = [];
    if (filter?.status?.length) constraints.push(where('status', 'in', filter.status));
    if (filter?.assignedTo)
      constraints.push(where('assignedStaff', 'array-contains', filter.assignedTo));
    if (filter?.limit) constraints.push(fsLimit(filter.limit));
    const ref = collection(db, COLLECTION);
    const snap = await getDocs(constraints.length ? query(ref, ...constraints) : query(ref));
    return snap.docs.map((d) => toOrder(d.data() as Record<string, unknown>));
  },
};
